import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, Phone, Clock, CalendarCheck, ArrowUpRight } from "lucide-react";
import { useSite, fullAddress, What3Words } from "@/lib/site";
import { Reveal, SectionLabel } from "@/components/site/Reveal";
import { readConsent } from "@/components/site/CookieConsent";

function useMapAllowed() {
  const [allowed, setAllowed] = useState(() => !!readConsent()?.marketing);
  useEffect(() => {
    const h = () => { if (readConsent()?.marketing) setAllowed(true); };
    window.addEventListener("c4x4:consent", h);
    return () => window.removeEventListener("c4x4:consent", h);
  }, []);
  return [allowed, () => setAllowed(true)];
}

export function MapPanel() {
  const site = useSite();
  const [allowed, allow] = useMapAllowed();
  if (site.map_embed_url && allowed) {
    return <iframe title="Map to Cirencester 4x4" src={site.map_embed_url} className="h-full min-h-[380px] w-full border-0" loading="lazy" data-testid="map-embed" />;
  }
  const gated = !!site.map_embed_url;
  return (
    <div className="relative h-full min-h-[380px] overflow-hidden bg-stone-light" data-testid={gated ? "map-consent" : "map-placeholder"}>
      <svg className="absolute inset-0 h-full w-full" viewBox="0 0 600 500" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
        <g fill="none" stroke="#C9BFAB" strokeWidth="1.2">
          <path d="M-20 120 C 120 140, 220 60, 360 110 S 560 180, 640 140" />
          <path d="M-20 300 C 140 260, 240 340, 380 290 S 540 230, 640 280" />
          <path d="M120 -20 C 160 120, 120 260, 210 380 S 260 520, 240 540" />
          <path d="M420 -20 C 380 100, 460 220, 400 330 S 420 480, 470 540" strokeDasharray="4 6" />
        </g>
        <g fill="none" stroke="#A6A39B" strokeWidth=".6" opacity=".6">
          {Array.from({ length: 9 }).map((_, i) => <path key={i} d={`M0 ${40 + i * 55} H600`} />)}
        </g>
      </svg>
      <div className="absolute left-1/2 top-1/2 w-[85%] max-w-sm -translate-x-1/2 -translate-y-1/2 text-center">
        <span className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-forest text-paper shadow-lg"><MapPin className="h-6 w-6" strokeWidth={1.5} /></span>
        <p className="mt-3 font-serif text-2xl text-forest">Cirencester</p>
        {gated ? (<>
          <button type="button" onClick={allow} className="btn btn-primary mt-5 !py-3" data-testid="map-load-button">Load map</button>
          <p className="mt-4 text-xs leading-relaxed text-charcoal/75">The map is provided by Google, which may set cookies. <Link to="/legal/cookies" className="underline">Cookie Policy</Link></p>
        </>) : <p className="text-xs uppercase tracking-[0.18em] text-charcoal/70">Map appears once premises are entered</p>}
      </div>
    </div>
  );
}

export default function LocationBlock({ index = "06" }) {
  const site = useSite();
  return (
    <section className="bg-stone/40" data-testid="location-section">
      <div className="mx-auto grid max-w-[1440px] gap-12 px-5 py-24 sm:px-8 lg:grid-cols-12 lg:px-12 lg:py-32">
        <Reveal className="lg:col-span-5">
          <SectionLabel index={index}>Visit us</SectionLabel>
          <h2 className="mt-6 font-serif text-4xl leading-[1.05] text-charcoal sm:text-5xl lg:text-6xl">Easy to reach. Worth the journey.</h2>
          <p className="mt-6 text-base leading-relaxed text-charcoal/80">
            We are based in Cirencester, Gloucestershire — serving the Cotswolds, Cheltenham, Gloucester, Swindon, Wiltshire and Oxfordshire, with easy access from the A417, A419 and A429.
          </p>
          <ul className="mt-10 space-y-5 text-sm">
            <li className="flex gap-4"><MapPin className="h-5 w-5 shrink-0 text-forest" strokeWidth={1.5} /><span>{fullAddress(site)}<What3Words site={site} className="mt-1 text-forest" testid="location-w3w" /></span></li>
            <li className="flex gap-4"><Phone className="h-5 w-5 shrink-0 text-forest" strokeWidth={1.5} /><a href={`tel:${site.phone_href}`} className="link-u" data-testid="location-phone">{site.phone}</a></li>
            <li className="flex gap-4"><Clock className="h-5 w-5 shrink-0 text-forest" strokeWidth={1.5} />
              <div className="w-full max-w-xs space-y-1">{(site.opening_hours || []).map((h) => <p key={h.day} className="flex justify-between gap-4"><span>{h.day}</span><span className="text-charcoal/75">{h.hours}</span></p>)}</div>
            </li>
            <li className="flex gap-4"><CalendarCheck className="h-5 w-5 shrink-0 text-forest" strokeWidth={1.5} /><span>By appointment only — we’ll have the vehicle ready for you.</span></li>
          </ul>
          <div className="mt-10 flex flex-wrap gap-3">
            <a href={site.directions_url} target="_blank" rel="noreferrer" className="btn btn-primary" data-testid="location-directions">Get Directions <ArrowUpRight className="arrow h-4 w-4" /></a>
            <Link to="/contact" className="btn btn-secondary" data-testid="location-book">Book a Viewing</Link>
          </div>
        </Reveal>
        <Reveal className="lg:col-span-6 lg:col-start-7" delay={0.1}><MapPanel /></Reveal>
      </div>
    </section>
  );
}
